// Setup UX pass 2026-10-21 (docs/design/2026-10-21-setup-ux-pass.md): every Setup screen, and
// Account and My subscriptions, opens with Work OS's 56 px `LocationHeader` (crumb Setup › title,
// a count, the primary action on the right) and runs edge to edge in the main region. The body
// is a centred 1040 px column for forms and groups (`width="doc"`, Linear's settings), or the
// full width for a dense table (`width="full"`, with a bleed `Toolbar` above it). No eyebrow,
// no 28 px title. SETUP-REGROUP: the Setup catalog's description of the current path says what
// the page is for (UX-A: behind an (i) beside the title; none on a page the catalog does not list).
import { type LocationCrumb, LocationHeader } from "@companyos/ui";
import type { ComponentType, ReactNode } from "react";
import { withSetupDescription } from "./setup-description";

type IconType = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export type SetupPageProps = {
  /** The crumb line; `["Setup"]` by default, a string is a plain crumb. */
  crumbs?: (LocationCrumb | string)[];
  icon?: IconType;
  title: ReactNode;
  count?: number;
  badge?: ReactNode;
  /** Right of the title: 28 px ghost buttons, the primary `size="sm"` last. */
  actions?: ReactNode;
  /** A 40 px `Toolbar bleed` (or tabs) between the header and the body, full width. */
  toolbar?: ReactNode;
  /** `doc`: a centred 1040 px column, 20 px in; `full`: edge to edge (tables). */
  width?: "doc" | "full";
  /** Anything docked under the body (a `SaveDock` is fixed and may go anywhere). */
  children: ReactNode;
  /** The `data-slot` of the page (tests and the visual suite look it up). */
  slot?: string;
};

const BODY = {
  doc: "mx-auto flex w-full max-w-[1040px] flex-col gap-4 px-6 py-5 max-md:px-4 max-md:py-4",
  full: "flex min-w-0 flex-1 flex-col",
} as const;

export function SetupPage({
  crumbs = ["Settings"],
  icon,
  title,
  count,
  badge,
  actions,
  toolbar,
  width = "doc",
  children,
  slot = "setup-page",
}: SetupPageProps) {
  const trail: LocationCrumb[] = crumbs.map((c) => (typeof c === "string" ? { label: c } : c));
  return (
    <div data-slot={slot} data-width={width} className="flex min-h-full min-w-0 flex-col">
      <LocationHeader
        crumbs={trail}
        {...(icon ? { icon } : {})}
        title={title}
        {...(count !== undefined ? { count } : {})}
        badge={withSetupDescription(badge)}
        {...(actions ? { actions } : {})}
      />
      {toolbar}
      <div data-slot="setup-body" className={BODY[width]}>
        {children}
      </div>
    </div>
  );
}
